import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as multiplayer from '../multiplayer';
import { emptyTerrain, terrainSolids } from '../engine/battlefield/presets';
import { mergeTokens } from '../engine/battlefield/tokens';
import { groundLevel } from '../engine/battlefield/excavation';
import { packGrid, packCells } from '../engine/battlefield/gridCodec';
import { unpackTerrain } from '../engine/battlefield/mapFile';
import { oneSurfacePerCell } from '../engine/battlefield/zones';
import {
  preserveMarker,
  preserveTeamArrivals,
  readTeamArrivals,
  deploy as deployTokens,
} from '../engine/battlefield/deployment';
import useBattlefieldLibrary from './useBattlefieldLibrary';
import { settleOnMap } from '../engine/objects/settleOnMap';

/** How long a drag settles before it is written. */
const DRAG_DEBOUNCE_MS = 120;

/**
 * A terrain document on its way out: the grid packed into one field.
 *
 * Firestore indexes every key of a map field, so a cell-per-key grid
 * costs an index entry per cube and is refused somewhere past forty
 * thousand of them. Packed, the whole grid is one field. See
 * `engine/battlefield/gridCodec.ts`.
 */
function writeTerrain(terrain) {
  if (!terrain?.voxels) return terrain;
  return { ...terrain, voxels: packGrid(terrain.voxels) };
}

/** And on its way in: the map file's own reader - both grid shapes, one surface per cell. */
const readTerrain = unpackTerrain;

/**
 * Tell somebody. There is no undo for terrain, so a build that did not
 * save has to say so while the person who made it is still looking.
 */
function reportTerrainWriteFailure(cells, err) {
  console.error('Battlefield terrain write failed', err);
  multiplayer.reportWriteFailure?.(
    `The map could not be saved (${cells.toLocaleString()} cubes). It is still on screen but has NOT reached the session - copy anything you need before reloading.`,
    err,
  );
}

/** A grid's cells, split by whether their material is GM-only. */
function splitCells(grid) {
  const openCells = {};
  const secretCells = {};
  for (const [key, materialKey] of Object.entries(grid.cells || {})) {
    const hidden = grid.materials?.[materialKey]?.hidden;
    (hidden ? secretCells : openCells)[key] = materialKey;
  }
  return { openCells, secretCells };
}

/**
 * Split terrain into what players may see and what only the GM may.
 *
 * Three things can be secret: a parametric solid flagged `hidden`, any
 * CUBE built from a material flagged `hidden`, and a painted ZONE flagged
 * `hidden`. Each was once shipped to the players' own client while only
 * hidden on screen - the cubes until they were split, the zones until
 * 2026-10-07 (plan map-display, G1): a trap painted GM-only was readable
 * from a console.
 */
function splitSecretTerrain(terrain) {
  const solids = terrain.solids || [];
  const zones = terrain.zones || [];
  const open = { ...terrain, solids: solids.filter((s) => !s.hidden), zones: zones.filter((z) => !z.hidden) };
  const secret = { solids: solids.filter((s) => s.hidden), zones: zones.filter((z) => z.hidden) };
  const grid = terrain.voxels;
  if (!grid) return { open, secret };
  const { openCells, secretCells } = splitCells(grid);
  open.voxels = { ...grid, cells: openCells };
  // The GM half carries the materials too, or the cells arrive with
  // nothing to say what they are made of.
  secret.voxels = { ...grid, cells: secretCells, dug: {} };
  return { open, secret };
}

/**
 * How a map edit settles tokens when nobody says otherwise: on the terrain alone. The shell injects one that also
 * meets the Objects' boxes (`settleOnMap`), since only it knows the roster.
 */
export const settleOnTerrain = (tokens, terrain) => settleOnMap(tokens, terrain, null, groundAtFor(terrain));

/** The ground height under a point of this terrain - 0 where it has no grid. */
export function groundAtFor(terrain) {
  const size = terrain?.voxels?.size > 0 ? terrain.voxels.size : 1;
  return (x, z) => (terrain?.voxels ? groundLevel(terrain.voxels, Math.floor(x / size), Math.floor(z / size)) : 0);
}

/**
 * The GM's own view: the shared terrain plus the secrets. Players never
 * receive the second half at all, so there is nothing for them to
 * reassemble. The shared terrain itself when there are no secrets.
 */
function withSecrets(terrain, gmTerrain) {
  const secretSolids = gmTerrain.solids || [];
  const secretZones = gmTerrain.zones || [];
  const secretCells = gmTerrain.voxels?.cells || {};
  if (!secretSolids.length && !secretZones.length && !Object.keys(secretCells).length) return terrain;
  return {
    ...terrain,
    solids: [...(terrain.solids || []), ...secretSolids],
    // One surface per cell on the MERGED view too: each half is clean alone, but a visible zone and a hidden one
    // may share cells on an old document (the secret half is the later, as it is painted over the open one).
    zones: oneSurfacePerCell([...(terrain.zones || []), ...secretZones]),
    voxels: terrain.voxels
      ? { ...terrain.voxels, cells: { ...terrain.voxels.cells, ...secretCells } }
      : gmTerrain.voxels,
  };
}

// ---- Terrain ----

/** The shared terrain, and - for the GM only - the secret half. */
function useTerrainListeners(sessionId, isGm) {
  const [terrain, setTerrainState] = useState(emptyTerrain);
  const [gmTerrain, setGmTerrainState] = useState(null);
  useEffect(() => {
    if (!sessionId) return undefined;
    return multiplayer.onBattlefieldTerrainUpdate(sessionId, (data, exists) => {
      if (exists && data) setTerrainState(readTerrain(data));
    });
  }, [sessionId]);
  useEffect(() => {
    if (!sessionId || !isGm) return undefined;
    return multiplayer.onGmBattlefieldTerrainUpdate(sessionId, (data, exists) => {
      setGmTerrainState(exists && data ? readTerrain(data) : null);
    });
  }, [sessionId, isGm]);
  return { terrain, setTerrainState, gmTerrain };
}

/**
 * Write the terrain, SPLIT by who is allowed to see it - and settle every
 * token onto it first.
 *
 * Solids marked `hidden` go to `battlefieldGm/terrain`, which the rules
 * keep players out of; everything else goes to the shared document. This
 * is the difference between fog of war and a flag: `isMember` grants read
 * on everything under `shared/`, so an ambush left in the shared document
 * is readable by any player with a console. The split happens HERE so that
 * no path can write a secret to the wrong document.
 *
 * A REJECTED WRITE MUST NEVER LOOK LIKE A DELETION: it used to land in
 * `.catch(console.error)`, the listener then delivered the server's older
 * copy, and a tower that was never saved appeared to be deleted.
 *
 * Returns what settling did (`{ lifted, movedInside }`), so a map load names who it moved without settling twice.
 */
function useSetTerrain(sessionId, { terrain, setTerrainState }, settleTokens) {
  return useCallback(
    (next, options) => {
      const value = typeof next === 'function' ? next(terrain) : next;
      setTerrainState(value);
      const settled = settleTokens(value, options?.roster);
      if (!sessionId) return settled;
      const { open, secret } = splitSecretTerrain(value);
      const cells = packCells(value.voxels?.cells).count;
      const blame = (err) => reportTerrainWriteFailure(cells, err);
      multiplayer.setBattlefieldTerrain(sessionId, writeTerrain(open)).catch(blame);
      multiplayer.setGmBattlefieldTerrain(sessionId, writeTerrain(secret)).catch(blame);
      return settled;
    },
    [sessionId, terrain, setTerrainState, settleTokens],
  );
}

/**
 * Settle every token onto the terrain as it now is.
 *
 * Called on EVERY terrain change, not just on loading a map. Building a
 * platform under someone should lift them and erasing it should set them
 * down; without this they hang in the air at whatever height the ground
 * used to be. Only tokens that actually moved are written, at once -
 * `reconcileTokens` returns the very same object for anyone who did not,
 * so a change that moves nobody costs no writes at all.
 *
 * ON THE OBJECTS' BOXES TOO (2026-10-26): `settle` is injected by the shell, which knows the roster and every token
 * (the GM's hidden ones included, which this layer never sees), so a marine on a generator's box stays on it through
 * an edit elsewhere. `roster`, when given, is the cast this map arrives with (a scene cut's), which the shell has
 * not seen yet.
 */
function useSettleTokens(writeToken, tokensRef, settle) {
  return useCallback(
    (value, roster) => {
      const current = tokensRef.current;
      const { tokens: settled, lifted, movedInside } = settle(current, value, roster);
      for (const token of Object.values(settled)) {
        if (current[token.id] !== token) writeToken(token, { immediate: true });
      }
      return { lifted, movedInside };
    },
    [writeToken, tokensRef, settle],
  );
}

// ---- Tokens ----

/**
 * Replace the token map, keeping `tokensRef` - which settling reads - in step. The ref is updated at WRITE time, not
 * at render time, because settling reads it in the same act (a cut: leavers off, the ring, then the new ground -
 * gotchas 2026-10-21). Every mutation goes through here.
 */
function useTokenState() {
  const [tokens, setTokens] = useState({});
  const tokensRef = useRef({});
  const updateTokens = useCallback((fn) => {
    const next = fn(tokensRef.current);
    if (next === tokensRef.current) return;
    tokensRef.current = next;
    setTokens(next);
  }, []);
  return { tokens, tokensRef, updateTokens };
}

/**
 * ONE LISTENER, TWO REDUCERS. Pings share this collection with tokens and
 * are told apart by `kind`; each side must be equally strict - a ping
 * reaching `mergeTokens` would be a marker nobody can move. `mergeTokens`
 * runs inside the updater against the CURRENT local map, and is told only
 * what this snapshot actually knows: mid-burst it lists only the writes
 * that have surfaced so far, so only an announced removal counts.
 */
function useTokenListener(sessionId, onPings, updateTokens, seenRef) {
  useEffect(() => {
    if (!sessionId) return undefined;
    seenRef.current = new Set();
    return multiplayer.onBattlefieldTokensUpdate(sessionId, ({ listsEveryone: authoritative, removed, docs }) => {
      onPings?.(docs.filter((d) => d?.kind === 'ping'));
      const remote = docs.filter((t) => t?.kind === 'token');
      noteSeen(seenRef.current, remote, { authoritative, removed });
      updateTokens((local) => mergeTokens(local, remote, { authoritative, removed }));
    });
  }, [sessionId, onPings, updateTokens, seenRef]);
}

/**
 * The tokens the server has shown this screen, and so EXIST there (2026-10-17): a write onto one is an update,
 * never a create (multiplayer `setBattlefieldToken`), so a late write cannot bring back a token deleted elsewhere.
 * A token a view stops listing, or announces removed, is forgotten - a re-deploy then creates it again.
 */
function noteSeen(seen, remote, { authoritative, removed }) {
  const listed = new Set(remote.map((t) => t.id));
  for (const id of listed) seen.add(id);
  for (const id of removed ?? []) seen.delete(id);
  if (authoritative) for (const id of [...seen]) if (!listed.has(id)) seen.delete(id);
}

/**
 * The drag queue: the latest position per token, sent together. Solo
 * play sends nothing. A drag interrupted by an unmount must not strand
 * its timer.
 */
function useTokenQueue(sessionId, seenRef) {
  const pending = useRef(new Map());
  const flushTimer = useRef(null);
  const flush = useCallback(() => {
    flushTimer.current = null;
    if (sessionId) {
      for (const token of pending.current.values())
        multiplayer.setBattlefieldToken(sessionId, token, seenRef.current.has(token.id)).catch(console.error);
    }
    pending.current.clear();
  }, [sessionId, seenRef]);
  useEffect(
    () => () => {
      if (flushTimer.current) clearTimeout(flushTimer.current);
    },
    [],
  );
  return { pending, flushTimer, flush };
}

/** Token writes, coalescing the flood a drag produces: one write per token per 120ms, or at once when `immediate`. */
function useTokenWriter(sessionId, updateTokens, seenRef) {
  const { pending, flushTimer, flush } = useTokenQueue(sessionId, seenRef);
  const writeToken = useCallback(
    (token, { immediate = false } = {}) => {
      updateTokens((prev) => ({ ...prev, [token.id]: token }));
      pending.current.set(token.id, token);
      if (!immediate) {
        if (!flushTimer.current) flushTimer.current = setTimeout(flush, DRAG_DEBOUNCE_MS);
        return;
      }
      if (flushTimer.current) clearTimeout(flushTimer.current);
      flush();
    },
    [flush, updateTokens, pending, flushTimer],
  );
  return { writeToken, pending };
}

/** Take a token off the map: its queued write is dropped, and it is deleted. */
function useRemoveToken(sessionId, updateTokens, pending, seenRef) {
  return useCallback(
    (tokenId) => {
      pending.current.delete(tokenId);
      seenRef.current.delete(tokenId); // a re-deploy creates it afresh
      updateTokens((prev) => {
        const next = { ...prev };
        delete next[tokenId];
        return next;
      });
      if (sessionId) multiplayer.deleteBattlefieldToken(sessionId, tokenId).catch(console.error);
    },
    [sessionId, updateTokens, pending, seenRef],
  );
}

// ---- The deployment marker ----

/**
 * DO NOT LET MY OWN ECHOES REWIND ME. Raising the banner writes 1, 2, 3,
 * 4, 5 m in quick succession, and each write comes back as its own
 * update; applying those in turn walks the banner back down through every
 * intermediate value. Firestore hides this by folding a client's pending
 * writes into one local snapshot; a backend that echoes each write
 * separately does not, and it is not the adapter's to paper over - the
 * `listsEveryone` invariant, held locally for one document: while any of
 * my writes is unacknowledged, a remote view is not news.
 */
function useGuardedDoc(sessionId, { watch, write, read }) {
  const [value, setValue] = useState(() => read(null));
  const writesInFlight = useRef(0);
  useEffect(() => {
    if (!sessionId) return undefined;
    return watch(sessionId, (data, exists) => {
      if (writesInFlight.current > 0) return;
      setValue(read(exists ? data : null));
    });
  }, [sessionId, watch, read]);
  const writeValue = useCallback(
    (next) => {
      setValue(next);
      if (!sessionId) return;
      writesInFlight.current += 1;
      write(sessionId, next)
        .catch(console.error)
        .finally(() => {
          writesInFlight.current -= 1;
        });
    },
    [sessionId, write],
  );
  return [value, writeValue];
}

// The two documents, each read from `multiplayer` when used - never at import, where a test's partial mock of it
// would refuse the property.
/** The main marker (`shared/deployment`), or null. */
const DEPLOYMENT_DOC = {
  watch: (id, cb) => multiplayer.onDeploymentUpdate(id, cb),
  write: (id, next) => multiplayer.setDeployment(id, next),
  read: (data) => data || null,
};

/** Each team's own arrival point (`shared/arrivals`, plan scene-editor-library, B), or `{}`. */
const ARRIVALS_DOC = {
  watch: (id, cb) => multiplayer.onArrivalsUpdate(id, cb),
  write: (id, next) => multiplayer.setArrivals(id, next),
  read: readTeamArrivals,
};

function useDeployment(sessionId) {
  const [deployment, writeDeployment] = useGuardedDoc(sessionId, DEPLOYMENT_DOC);
  const [arrivals, writeArrivals] = useGuardedDoc(sessionId, ARRIVALS_DOC);
  return { deployment, writeDeployment, arrivals, writeArrivals };
}

// ---- Swapping the map ----

/**
 * RE-RING EVERYONE around the marker, when the table has asked for it. A
 * formation laid out for a courtyard means nothing inside a hive block,
 * and keeping it is only right when the two maps are related - off by
 * default, because silently rearranging a fight is worse.
 *
 * On the terrain alone: the Objects are re-ringed with everyone, so their boxes where they stood are no obstacle.
 */
function redeployOnto(next, marker, tokensRef, avoid, writeToken) {
  const out = deployTokens(
    Object.values(tokensRef.current),
    marker,
    next.boundary,
    terrainSolids(next),
    avoid,
    groundAtFor(next),
  );
  for (const token of Object.values(out.placed)) writeToken(token, { immediate: true });
  return Object.keys(out.placed);
}

/**
 * Swap the map mid-fight.
 *
 * ONE write to the terrain document. Tokens are separate documents, so
 * wounds, conditions and initiative survive by construction. Positions are
 * kept; anyone the new terrain arrived underneath is stood on top of it
 * and named, so the GM can move them deliberately. The marker survives
 * too - `preserveMarker` clamps it into a smaller arena or takes an
 * authored map's own, and returns the SAME OBJECT when nothing changed,
 * which keeps this from writing on every load.
 *
 * `rules` arrives as an ARGUMENT rather than a hook parameter because the
 * house rules are resolved from `permissions`, which comes from a sync
 * hook that itself depends on this one.
 */
function useLoadMap({ setTerrain, onLog, points, tokensRef, writeToken }) {
  const { deployment, writeDeployment, arrivals, writeArrivals } = points;
  return useCallback(
    (next, rules = {}) => {
      if (!next) return;
      const { redeployOnMapLoad = false, avoid } = rules;
      const keptMarker = preserveMarker(deployment, next);
      if (keptMarker !== deployment) writeDeployment(keptMarker);
      // A team's arrival point is a marker too: kept, or pulled into a smaller arena.
      const keptPoints = preserveTeamArrivals(arrivals, next);
      if (keptPoints !== arrivals) writeArrivals(keptPoints);
      if (redeployOnMapLoad && keptMarker) {
        const redeployed = redeployOnto(next, keptMarker, tokensRef, avoid, writeToken);
        setTerrain(next);
        onLog?.([], { lifted: [], movedInside: [], redeployed });
        return;
      }
      // setTerrain settles everyone and says who it moved; loading a map only adds the log line, because a swap
      // is the one case worth telling the table about.
      const { lifted, movedInside } = setTerrain(next);
      const names = [...lifted, ...movedInside];
      if (names.length) onLog?.(names, { lifted, movedInside });
    },
    [setTerrain, onLog, deployment, writeDeployment, arrivals, writeArrivals, tokensRef, writeToken],
  );
}

/**
 * useBattlefieldSync - the battlefield, replicated.
 *
 * Terrain is one document: rare, deliberate, atomic. Tokens are a
 * document each and carry a `rev` clock, applied as a veto by
 * `mergeTokens` - with many clients writing at drag frequency, your own
 * late echo is otherwise indistinguishable from someone else's move, and
 * taking it yanks the token backwards under your cursor.
 *
 * NOTHING HERE ENTERS UNDO. Movement is a deliberate edit, and the shared
 * history stack is capped at 30 - drag traffic would evict real combat
 * history in seconds.
 *
 * Solo play keeps everything in local state, so the map works with no
 * session at all.
 */
export default function useBattlefieldSync({ sessionId, isGm, onLog, onPings, settle = settleOnTerrain }) {
  const terrainState = useTerrainListeners(sessionId, isGm);
  const { tokens, tokensRef, updateTokens } = useTokenState();
  const seenRef = useRef(new Set());
  useTokenListener(sessionId, onPings, updateTokens, seenRef);
  const points = useDeployment(sessionId);
  const { deployment, writeDeployment, arrivals, writeArrivals } = points;
  const { writeToken, pending } = useTokenWriter(sessionId, updateTokens, seenRef);
  const removeToken = useRemoveToken(sessionId, updateTokens, pending, seenRef);
  const settleTokens = useSettleTokens(writeToken, tokensRef, settle);
  const setTerrain = useSetTerrain(sessionId, terrainState, settleTokens);
  const { terrain, gmTerrain } = terrainState;
  const visibleTerrain = useMemo(
    () => (isGm && gmTerrain ? withSecrets(terrain, gmTerrain) : terrain),
    [terrain, gmTerrain, isGm],
  );
  const { library, ...verbs } = useBattlefieldLibrary(sessionId, visibleTerrain);
  const loadMap = useLoadMap({ setTerrain, onLog, points, tokensRef, writeToken });
  return {
    ...{ terrain: visibleTerrain, setTerrain, gmTerrain, tokens, writeToken, removeToken },
    ...{ library, deployment, setDeployment: writeDeployment, arrivals, setArrivals: writeArrivals, ...verbs, loadMap },
  };
}
